import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Download, Pencil, Archive } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { api, errMsg, fmtDate, fmtDT, inr, toCSV, useFetch } from "../../lib/api";
import { useApp } from "../../lib/ctx";
import { StatusBadge, Loader, EmptyState, Bar, Field } from "./kit";
import RecordForm from "./RecordForm";
import { FileList } from "./Files";

export function cell(col, row, users = []) {
  const v = col.key.split(".").reduce((o, k) => (o ? o[k] : undefined), row);
  if (col.render) return col.render(row);
  if (col.type === "money") return <span className="tabular-nums">{inr(v)}</span>;
  if (col.type === "date") return fmtDate(v);
  if (col.type === "status") return <StatusBadge value={v} />;
  if (col.type === "progress") return <div className="flex items-center gap-2 min-w-[110px]"><Bar value={v} className="flex-1" /><span className="text-xs">{Math.round(v || 0)}%</span></div>;
  if (col.type === "user") return users.find((u) => u.user_id === v)?.name || "—";
  return v ?? "—";
}

export function DataTable({ rows, columns, onRow, testid = "data-table" }) {
  const { users } = useApp();
  return (
    <div className="overflow-x-auto">
      <table className="tbl w-full" data-testid={testid}>
        <thead><tr>{columns.map((c) => <th key={c.key}>{c.label}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} onClick={() => onRow && onRow(r)} className={onRow ? "cursor-pointer" : ""} data-testid={`row-${r.id}`}>
              {columns.map((c) => <td key={c.key}>{cell(c, r, users)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ActionsHost = ({ fn, row, done }) => fn(row, done);

export function RecordDrawer({ open, onClose, row, tab, reload }) {
  const { me, users } = useApp();
  const [edit, setEdit] = useState(false);
  const hist = useFetch(open && row ? `/e/${tab.entity}/${row.id}/history` : null, [row?.id, open]);
  if (!row) return null;
  const mgr = ["admin", "manager"].includes(me?.role);
  const archive = async () => {
    if (!window.confirm("Archive this record? It will be hidden from lists but kept for audit.")) return;
    try { await api.post(`/e/${tab.entity}/${row.id}/archive`); toast.success("Archived"); onClose(); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="record-drawer">
        <SheetHeader>
          <p className="lbl">{tab.label}{row.number ? ` · ${row.number}` : ""}</p>
          <SheetTitle className="text-xl">{row.name || row.title || row.number || row.summary?.slice(0, 40)}</SheetTitle>
          <div className="flex gap-2 flex-wrap">{row.status && <StatusBadge value={row.status} />}{row.priority && <StatusBadge value={row.priority} />}</div>
        </SheetHeader>
        <div className="flex gap-2 mt-4 flex-wrap">
          {tab.editable !== false && <button className="btn-outline-o" onClick={() => setEdit(true)} data-testid="drawer-edit-btn"><Pencil className="h-4 w-4" />Edit</button>}
          {tab.actions && <ActionsHost fn={tab.actions} row={row} done={() => { reload(); onClose(); }} />}
          {mgr && tab.archivable !== false && <button className="btn-outline-o !border-neutral-300 !text-neutral-600" onClick={archive} data-testid="drawer-archive-btn"><Archive className="h-4 w-4" />Archive</button>}
        </div>
        <div className="grid grid-cols-2 gap-4 mt-6">
          {tab.fields.filter((f) => !f.hidden && f.type !== "items").map((f) => (
            <Field key={f.key} label={f.label} value={f.type === "user" ? users.find((u) => u.user_id === row[f.key])?.name
              : f.type === "ref" ? (row[f.nameKey || f.key.replace("_id", "_name")] || (row[f.key] ? "Linked" : null))
              : f.type === "number" && f.money ? inr(row[f.key]) : f.type === "checkbox" ? (row[f.key] ? "Yes" : "No") : f.type === "date" ? fmtDate(row[f.key]) : row[f.key]} />
          ))}
          <Field label="Created" value={`${fmtDT(row.created_at)} by ${row.created_by_name || "—"}`} />
        </div>
        {tab.detail && <div className="mt-6">{tab.detail(row, reload)}</div>}
        {tab.files !== false && <div className="mt-6"><p className="h3 mb-2">Attachments</p>
          <FileList entity={tab.entity} entityId={row.id} projectId={row.project_id} category={tab.fileCategory} /></div>}
        <div className="mt-6"><p className="h3 mb-2">History</p>
          {(hist.data || []).map((h) => (
            <div key={h.id} className="text-xs border-l-2 border-orange-200 pl-3 py-1.5">
              <span className="font-medium">{h.action}</span> by {h.actor_name} · {fmtDT(h.at)}
              {Object.keys(h.changes || {}).length > 0 && <span className="text-neutral-500"> — {Object.keys(h.changes).join(", ")}</span>}
            </div>
          ))}
        </div>
        <RecordForm open={edit} onClose={() => setEdit(false)} entity={tab.entity} fields={tab.fields.filter((f) => !f.createOnly)} record={row}
          title={`Edit ${tab.single || "record"}`} onSaved={() => { reload(); onClose(); }} />
      </SheetContent>
    </Sheet>
  );
}

export function EntityTab({ tab, query = "", extraHeader }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [creating, setCreating] = useState(false);
  const [sel, setSel] = useState(null);
  const { data, loading, error, reload } = useFetch(`/e/${tab.entity}?${query}${tab.query ? "&" + tab.query : ""}`, [query]);
  const rows = useMemo(() => (data || []).filter((r) => (!status || r[tab.statusKey || "status"] === status) &&
    (!q || JSON.stringify(r).toLowerCase().includes(q.toLowerCase()))), [data, q, status, tab.statusKey]);
  const statuses = useMemo(() => [...new Set((data || []).map((r) => r[tab.statusKey || "status"]).filter(Boolean))], [data, tab.statusKey]);
  return (
    <div className="card-c fade-in">
      <div className="flex flex-col md:flex-row gap-3 md:items-center justify-between p-4 border-b border-neutral-100">
        <div className="flex gap-2 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-3 text-neutral-400" />
            <input data-testid={`${tab.entity}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${tab.label.toLowerCase()}...`}
              className="h-10 w-full rounded-xl border border-input pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5A00]/30" />
          </div>
          {statuses.length > 0 && (
            <select data-testid={`${tab.entity}-status-filter`} value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-xl border border-input px-3 text-sm bg-white">
              <option value="">All statuses</option>{statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </div>
        <div className="flex gap-2">
          {extraHeader}
          <button className="btn-outline-o" onClick={() => toCSV(rows, tab.columns.map((c) => c.key))} data-testid={`${tab.entity}-export-btn`}><Download className="h-4 w-4" />CSV</button>
          {tab.canCreate !== false && <button className="btn-grad" onClick={() => setCreating(true)} data-testid={`${tab.entity}-create-btn`}><Plus className="h-4 w-4" />{tab.createLabel || "New"}</button>}
        </div>
      </div>
      {loading ? <Loader /> : error ? <EmptyState title="Could not load" text={error} /> : rows.length === 0 ?
        <EmptyState title={`No ${tab.label.toLowerCase()} yet`} text={tab.emptyText || "Records you create will appear here."}
          action={tab.canCreate !== false && <button className="btn-grad" onClick={() => setCreating(true)}><Plus className="h-4 w-4" />{tab.createLabel || "New"}</button>} /> :
        <DataTable rows={rows} columns={tab.columns} onRow={tab.onRow || setSel} testid={`${tab.entity}-table`} />}
      <RecordForm open={creating} onClose={() => setCreating(false)} entity={tab.entity} fields={tab.fields} initial={tab.initial || {}}
        title={tab.createLabel || `New ${tab.single || ""}`} onSaved={reload} endpoint={tab.endpoint} />
      <RecordDrawer open={!!sel} row={sel} tab={tab} onClose={() => setSel(null)} reload={reload} />
    </div>
  );
}

export function EntityModule({ title, subtitle, tabs, query = "" }) {
  const [sp, setSp] = useSearchParams();
  const active = sp.get("tab") || tabs[0].key;
  const tab = tabs.find((t) => t.key === active) || tabs[0];
  return (
    <div>
      <div className="flex gap-1 mb-5 overflow-x-auto border-b border-neutral-200" data-testid="module-tabs">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setSp({ tab: t.key })} data-testid={`tab-${t.key}`}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${t.key === tab.key ? "border-[#FF5A00] text-[#FF5A00]" : "border-transparent text-neutral-500 hover:text-[#0D131B]"}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab.custom ? tab.custom() : <EntityTab key={tab.key} tab={tab} query={query} />}
    </div>
  );
}
